import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setViewChats } from "src/store/slices/HomeSlice";
import { X } from "@phosphor-icons/react";
import {
  resetDeletedThread,
  resetDownloadUrl,
  resetUpdateActions,
  resetUpdatedTitle,
} from "src/store/slices/threadsSlice";
import DeleteThreadModal from "src/pages/Modals/DeleteThreadModal";
import { trackAnalytics } from "src/pages/helpers.js";
import TopNav from "../topnav/index.js";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants.js";
import { useNavigate } from "react-router-dom";
import ThreadListItem from "./ThreadListItem.js";
import SearchBar from "./SearchBar.js";
import { getFilteredResults } from "./helpers.js";
import { REQUEST_STATUS } from "src/domain/messages/requestStatus";

const ChatHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [actionOnThread, setActionOnThread] = useState("");
  const [updateThreadInfo, setUpdateThreadInfo] = useState({});
  const [updateActionLoading, setUpdateActionLoading] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [renameActiveId, setRenameActiveId] = useState(null);
  const [filteredResults, setFilteredResults] = useState({});
  const [textQuery, setTextQuery] = useState("");
  const [resultsExist, setResultsExist] = useState(false);
  const [hoveredThredId, setHoveredThreadId] = useState(null);

  const threads = useSelector((state) => state.threads.threads);
  const threadsStatus = useSelector((state) => state.threads.threads_status);
  const viewChats = useSelector((state) => state.homeslice.viewChats);
  const deletedThread = useSelector((state) => state.threads.deletedThread);
  const updatedTitle = useSelector((state) => state.threads.updatedTitle);
  const sidenavCollapsed = useSelector((state) => state.homeslice.sidenavCollapsed);
  const documentPanelOpen = useSelector((state) => state.messageSlice.documentPanelOpen);
  const effectiveCollapsed = sidenavCollapsed || documentPanelOpen;
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);

  const modalRef = useRef(null);

  const dropdownClicked = (index) => {
    setActiveIndex(index);
    dispatch(resetUpdateActions());
  };

  const onPerformThreadAction = (thread, action, duration = null, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    setUpdateThreadInfo({ thread: thread, duration: duration });
    setActionOnThread(action);

    switch (action) {
      case "Rename":
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: "Rename",
            [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.REQUEST,
            [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CHAT_HISTORY,
          },
          userMetadata,
        );
        dispatch(resetUpdatedTitle());
        setRenameActiveId(thread.id);
        break;
      case "Delete":
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: "Delete",
            [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.REQUEST,
            [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.CHAT_HISTORY,
          },
          userMetadata,
        );
        break;
      default:
        break;
    }
  };

  const handleClickOutside = (event) => {
    if (actionOnThread || renameActiveId) {
      return;
    }

    const clickedElement = event.target;
    const isDropdownClick = clickedElement.closest(".ant-dropdown");
    const isModalClick = clickedElement.closest(".ant-modal");

    if (isDropdownClick || isModalClick) {
      return;
    }

    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeChatHistory();
    }
  };

  const closeChatHistory = () => {
    dispatch(setViewChats(false));
  };

  useEffect(() => {
    const { uniqueResults, hasResults } = getFilteredResults(threads, textQuery);
    setFilteredResults(uniqueResults);
    setResultsExist(hasResults);
  }, [threads, textQuery]);

  useEffect(() => {
    if (deletedThread && deletedThread.id) {
      if (deletedThread.id === window.location.pathname.split("/")[2]) {
        dispatch(resetDeletedThread());
        setUpdateActionLoading(false);
        navigate("/home");
      }
    } else if (deletedThread && deletedThread.error) {
      alert("Failed to delete thread");
      setUpdateActionLoading(false);
    }
  }, [deletedThread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updatedTitle && updatedTitle.id) {
      setUpdateActionLoading(false);
      dispatch(resetDeletedThread());
    } else if (updatedTitle && updatedTitle.error) {
      alert("Failed to update thread title");
      setUpdateActionLoading(false);
    }
  }, [updatedTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setShowChatHistory(viewChats);
    dispatch(resetDownloadUrl());
  }, [viewChats]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showChatHistory) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showChatHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showChatHistory && (
        <div
          className={`fixed top-0 z-[51] bg-[#FCFCFC] left-0 ${effectiveCollapsed ? "md:left-[64px]" : "md:left-[228px]"
            }`}
        >
          <div className="md:hidden">
            <TopNav page="home" />
          </div>
          <div
            className="chat-history flex flex-col h-screen-dvh w-screen md:w-[464px] pt-[90px] select-none shadow-[4px_0_10px_-1px_rgba(0,0,0,0.02)] bg-white pr-[16px] pl-[24px] md:py-[24px] gap-[40px] pb-safe"
            ref={modalRef}
          >
            <div data-testid="chat-history-drawer" className="flex flex-col h-[77px] w-full gap-4">
              <div className="flex flex-row items-center justify-between bg-white">
                <h2
                  className="text-[16px] font-bold leading-[12px] cursor-default select-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  Your History
                </h2>
                <button data-testid="chat-history-close-btn" onClick={closeChatHistory} className="text-[#686868] cursor-pointer">
                  <X size={16} className="cursor-pointer" />
                </button>
              </div>
              <SearchBar textQuery={textQuery} setTextQuery={setTextQuery} />
            </div>

            {threadsStatus === REQUEST_STATUS.LOADING ? (
              <div className="flex flex-col gap-[32px]">
                {[
                  { widths: ["340px", "250px", "290px", "360px"] },
                  { widths: ["270px", "320px", "230px"] },
                  { widths: ["300px", "260px", "350px"] },
                ].map((group, gi) => (
                  <div key={gi}>
                    <div className="h-[12px] w-[80px] bg-gray-200 rounded animate-pulse" />
                    <ul className="mt-[16px] gap-[8px] flex flex-col">
                      {group.widths.map((w, i) => (
                        <li key={i} className="bg-white rounded-[8px]">
                          <div className="p-[12px] flex items-center gap-[8px]">
                            <div className="w-full">
                              <div className="h-[20px] rounded bg-gray-200 animate-pulse" style={{ maxWidth: w }} />
                              <div className="flex items-center gap-[6px] mt-[4px]">
                                <div className="h-[12px] w-[220px] rounded bg-gray-100 animate-pulse" />
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col overflow-y-scroll gap-[32px] pb-[150px]">
                {resultsExist ? (
                  <>
                    {Object.keys(filteredResults).map((duration) => (
                      <React.Fragment key={duration}>
                        {filteredResults[duration]?.length > 0 && (
                          <div>
                            <p className="uppercase text-[#1b2b48] text-[12px] leading-[12px] font-[700]">
                              {duration.replace(/_/g, " ")}
                            </p>
                            <ul className="mt-[16px] gap-[8px] flex flex-col">
                              {filteredResults[duration]
                                ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                ?.map((thread, index) => (
                                  <ThreadListItem
                                    key={thread.id}
                                    thread={thread}
                                    index={index}
                                    duration={duration}
                                    activeIndex={activeIndex}
                                    setActiveIndex={setActiveIndex}
                                    renameActiveId={renameActiveId}
                                    setRenameActiveId={setRenameActiveId}
                                    actionOnThread={actionOnThread}
                                    setActionOnThread={setActionOnThread}
                                    updateThreadInfo={updateThreadInfo}
                                    updateActionLoading={updateActionLoading}
                                    setUpdateActionLoading={setUpdateActionLoading}
                                    onPerformThreadAction={onPerformThreadAction}
                                    closeChatHistory={closeChatHistory}
                                    dropdownClicked={dropdownClicked}
                                    hoveredThredId={hoveredThredId}
                                    setHoveredThreadId={setHoveredThreadId}
                                  />
                                ))}
                            </ul>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <p className="text-center">You don't have any chats</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <DeleteThreadModal
        open={actionOnThread === "Delete"}
        setActionOnThread={setActionOnThread}
        threadInfo={updateThreadInfo}
        loading={updateActionLoading}
        setLoading={setUpdateActionLoading}
      />
    </>
  );
};

export default ChatHistory;
